import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { NavLink } from "react-router-dom";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  FileSearch,
  FileText,
  Layers,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
  CalendarDays,
  Building2,
  ChevronRight,
  Star,
} from "lucide-react";
import logoSrc from "../assets/logo.png";

// ─── Colour tokens (mirror Landing.tsx) ──────────────────────────────────────
const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  navyCard: "#11154a",
  navyLight: "#1a1f5e",
  purple: "#6C63FF",
  purpleDim: "#4B44CC",
  purpleGlow: "rgba(108,99,255,0.18)",
  lime: "#a8e063",
  limeGlow: "rgba(168,224,99,0.15)",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  cardBg: "rgba(17,21,74,0.6)",
  glassBg: "rgba(11,13,40,0.7)",
};

const CALENDLY = "https://cal.com/manageaze";

// ─── Animation helpers ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay } },
});

function InView({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={isInView ? "show" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Tier card data ───────────────────────────────────────────────────────────
const tiers = [
  {
    number: "01",
    name: "Policy Audit\n& Gap Analysis",
    tagline: "Understand where you stand.",
    color: C.purple,
    fee: "$3,000 – $6,000",
    timeline: "2–3 weeks",
    icon: <FileSearch size={22} />,
    highlight: false,
    deliverables: [
      "Review of up to 30 existing policies",
      "Assessment against SOC2, ISO 27001, HIPAA or custom framework",
      "Gap analysis report: what exists, what's missing, what's outdated",
      "Prioritised remediation roadmap with effort estimates",
      "1-hour readout session with stakeholders",
      "Policies catalogued inside ManagEaze — included",
    ],
  },
  {
    number: "02",
    name: "Policy\nBuild",
    tagline: "Start from scratch or rewrite.",
    color: C.lime,
    fee: "$8,000 – $18,000",
    timeline: "4–8 weeks",
    icon: <FileText size={22} />,
    highlight: true,
    deliverables: [
      "Everything in Tier 1",
      "Write or rewrite up to 20 policies",
      "AI-assisted drafting, human-reviewed & client-approved",
      "Aligned to one compliance framework of your choice",
      "Approval workflows configured in ManagEaze",
      "Role setup & onboarding for up to 10 users",
      "30-day post-delivery support window",
    ],
  },
  {
    number: "03",
    name: "Compliance\nFramework Setup",
    tagline: "Certification-ready, end to end.",
    color: C.purple,
    fee: "$20,000 – $40,000",
    timeline: "8–14 weeks",
    icon: <ShieldCheck size={22} />,
    highlight: false,
    deliverables: [
      "Everything in Tier 2",
      "Framework-specific policy set for target certification",
      "Control mapping: each policy to the relevant framework control",
      "Evidence packaging guidance for auditors",
      "Compliance task list created in ManagEaze",
      "Advisory support through pre-audit preparation",
      "Up to 3 stakeholder working sessions",
    ],
  },
];

// ─── How it works steps ───────────────────────────────────────────────────────
const steps = [
  { icon: <Users size={18} />, title: "Project kick-off", sub: "Your ManagEaze account is provisioned on day one." },
  { icon: <FileSearch size={18} />, title: "Audit & ingest", sub: "Existing policies ingested into your ManagEaze Policy Library." },
  { icon: <Sparkles size={18} />, title: "AI-assisted drafting", sub: "Policies drafted using the AI Policy Creator, grounded in your context." },
  { icon: <CheckCircle2 size={18} />, title: "Approval & sign-off", sub: "Stakeholders approve inside ManagEaze via configured workflows." },
  { icon: <Zap size={18} />, title: "Handover & live", sub: "You receive a fully populated, certified-ready ManagEaze environment." },
];

// ─── Who it's for ─────────────────────────────────────────────────────────────
const verticals = [
  { icon: <Building2 size={16} />, label: "Healthcare", note: "HIPAA · Clinical governance · Patient safety" },
  { icon: <ShieldCheck size={16} />, label: "Financial Services", note: "AML/KYC · Conduct frameworks · Regulatory compliance" },
  { icon: <Layers size={16} />, label: "Tech / SaaS", note: "SOC2 readiness · Infosec policies · Data handling" },
  { icon: <FileSearch size={16} />, label: "Audit preparation", note: "Any regulated industry approaching certification" },
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function Consulting() {
  return (
    <div
      style={{
        background: C.navy,
        color: C.white,
        fontFamily: "'DM Sans', ui-sans-serif, sans-serif",
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      {/* ─── NAV ─── */}
      <MarketingNav />

      {/* ─── HERO ─── */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-28 pb-20 overflow-hidden"
      >
        {/* Background orbs */}
        <div className="absolute rounded-full pointer-events-none" style={{ width: 600, height: 600, background: "radial-gradient(circle, rgba(108,99,255,0.13) 0%, transparent 70%)", top: "0%", left: "-5%", filter: "blur(70px)" }} />
        <div className="absolute rounded-full pointer-events-none" style={{ width: 400, height: 400, background: "radial-gradient(circle, rgba(168,224,99,0.08) 0%, transparent 70%)", bottom: "10%", right: "5%", filter: "blur(80px)" }} />

        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(108,99,255,0.15) 1px, transparent 1px)", backgroundSize: "30px 30px", maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black, transparent)" }} />

        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "backOut" }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 text-xs font-semibold"
          style={{ background: "rgba(108,99,255,0.12)", border: `1px solid ${C.border}`, color: C.purple }}
        >
          <Sparkles size={12} />
          Policy Management as a Service (PMaaS)
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-4xl mx-auto mb-6"
          style={{ lineHeight: 1.1 }}
        >
          <span className="block text-6xl md:text-8xl font-black tracking-tighter" style={{ color: C.white }}>
            We don't just give you
          </span>
          <span className="block text-6xl md:text-8xl font-black tracking-tighter" style={{ color: C.white }}>
            software —
          </span>
          <span
            className="block text-6xl md:text-8xl font-black tracking-tighter"
            style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.lime})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
          >
            we build your policies.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-2xl text-xl mb-10 leading-relaxed"
          style={{ color: C.dimSlate }}
        >
          PMaaS is ManagEaze's hands-on consulting offering. We audit, write, and structure your entire policy library — then hand you a live, certified-ready environment. Done for you, built to last.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-semibold transition-all hover:opacity-90 hover:scale-[1.02]"
            style={{ background: C.purple, color: C.white, boxShadow: `0 0 40px 4px rgba(108,99,255,0.25)` }}
          >
            <CalendarDays size={17} />
            Book a Free Scoping Call
          </a>
          <NavLink
            to="/welcome"
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-medium transition-all hover:opacity-80"
            style={{ color: C.slate, border: `1px solid ${C.border}` }}
          >
            View the Platform
            <ArrowRight size={16} />
          </NavLink>
        </motion.div>

        {/* Social proof strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-6 text-sm"
          style={{ color: C.dimSlate }}
        >
          {[
            "SOC2 · ISO 27001 · HIPAA",
            "Fixed-fee projects",
            "Platform included",
            "2–14 week delivery",
          ].map((item, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <CheckCircle2 size={12} style={{ color: C.lime }} />
              {item}
            </span>
          ))}
        </motion.div>
      </section>

      {/* ─── WHO IT'S FOR ─── */}
      <section className="px-6 py-20" style={{ background: C.navyMid }}>
        <div className="max-w-5xl mx-auto">
          <InView>
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: C.purple }}>
              Who it's for
            </p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" style={{ color: C.white }}>
              Built for regulated industries<br />facing a real deadline.
            </h2>
            <p className="text-lg mb-12 max-w-xl" style={{ color: C.dimSlate }}>
              PMaaS is relevant to any organisation where policies are scattered, outdated, or non-existent — especially when a certification or audit is approaching.
            </p>
          </InView>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {verticals.map((v, i) => (
              <InView key={v.label}>
                <motion.div
                  variants={stagger(i * 0.07)}
                  className="flex items-start gap-4 p-5 rounded-xl"
                  style={{ background: C.cardBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(108,99,255,0.15)", color: C.purple }}>
                    {v.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-base mb-0.5" style={{ color: C.white }}>{v.label}</div>
                    <div className="text-sm" style={{ color: C.dimSlate }}>{v.note}</div>
                  </div>
                </motion.div>
              </InView>
            ))}
          </div>

          {/* Use-case callouts */}
          <InView className="mt-8">
            <div className="p-5 rounded-xl" style={{ background: "rgba(168,224,99,0.06)", border: `1px solid rgba(168,224,99,0.18)` }}>
              <p className="text-sm" style={{ color: C.slate }}>
                <span className="font-semibold" style={{ color: C.lime }}>Common triggers:</span>{" "}
                Upcoming SOC2, ISO 27001 or HIPAA certification · Board or regulatory audit · Company has grown and informal norms need formalising · New compliance hire brought in to "fix the policy situation" · Recent compliance incident or near-miss
              </p>
            </div>
          </InView>
        </div>
      </section>

      {/* ─── SERVICE TIERS ─── */}
      <section className="px-6 py-24" style={{ background: C.navy }}>
        <div className="max-w-6xl mx-auto">
          <InView>
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: C.purple }}>Service Tiers</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ color: C.white }}>
              Three fixed-fee packages.
            </h2>
            <p className="text-lg mb-14 max-w-xl" style={{ color: C.dimSlate }}>
              Choose the tier that matches where you're starting from. Every engagement ends with a live ManagEaze environment.
            </p>
          </InView>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <InView key={tier.number}>
                <motion.div
                  variants={stagger(i * 0.1)}
                  className="flex flex-col rounded-2xl overflow-hidden h-full"
                  style={{
                    background: tier.highlight ? `linear-gradient(160deg, rgba(168,224,99,0.07) 0%, rgba(11,13,40,0.9) 60%)` : C.cardBg,
                    border: tier.highlight ? `1.5px solid rgba(168,224,99,0.35)` : `1px solid ${C.border}`,
                    backdropFilter: "blur(12px)",
                    boxShadow: tier.highlight ? `0 0 60px -10px rgba(168,224,99,0.15)` : "none",
                  }}
                >
                  {/* Card header */}
                  <div className="p-6 pb-5" style={{ borderBottom: `1px solid ${tier.highlight ? "rgba(168,224,99,0.15)" : C.border}` }}>
                    {tier.highlight && (
                      <div
                        className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3"
                        style={{ background: "rgba(168,224,99,0.15)", color: C.lime, border: `1px solid rgba(168,224,99,0.3)` }}
                      >
                        <Star size={9} fill={C.lime} />
                        Most Popular
                      </div>
                    )}

                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                      style={{ background: `${tier.color}18`, color: tier.color, border: `1px solid ${tier.color}30` }}
                    >
                      {tier.icon}
                    </div>

                    <div className="text-xs font-mono font-bold mb-1" style={{ color: tier.color }}>{tier.number}</div>
                    <h3
                      className="text-xl font-black tracking-tight mb-1 whitespace-pre-line"
                      style={{ color: C.white, lineHeight: 1.2 }}
                    >
                      {tier.name}
                    </h3>
                    <p className="text-sm" style={{ color: C.dimSlate }}>{tier.tagline}</p>
                  </div>

                  {/* Deliverables */}
                  <div className="flex-1 p-6">
                    <ul className="space-y-3">
                      {tier.deliverables.map((d, di) => (
                        <li key={di} className="flex items-start gap-2.5 text-base" style={{ color: C.slate }}>
                          <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" style={{ color: tier.color }} />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Footer */}
                  <div className="p-6 pt-4" style={{ borderTop: `1px solid ${tier.highlight ? "rgba(168,224,99,0.15)" : C.border}` }}>
                    <div className="flex items-end justify-between mb-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: C.dimSlate }}>Indicative fee</div>
                        <div className="text-base font-bold" style={{ color: tier.highlight ? C.lime : C.white }}>{tier.fee}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: C.dimSlate }}>Timeline</div>
                        <div className="flex items-center gap-1 text-sm font-medium" style={{ color: C.slate }}>
                          <Clock size={12} />
                          {tier.timeline}
                        </div>
                      </div>
                    </div>
                    <a
                      href={CALENDLY}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                      style={{
                        background: tier.highlight ? C.lime : "rgba(108,99,255,0.15)",
                        color: tier.highlight ? C.navy : C.purple,
                        border: tier.highlight ? "none" : `1px solid rgba(108,99,255,0.3)`,
                      }}
                    >
                      Talk to Us
                      <ArrowRight size={14} />
                    </a>
                  </div>
                </motion.div>
              </InView>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MAINTENANCE RETAINER ─── */}
      <section className="px-6 pb-20" style={{ background: C.navy }}>
        <div className="max-w-6xl mx-auto">
          <InView>
            <div
              className="rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8"
              style={{
                background: "linear-gradient(120deg, rgba(108,99,255,0.1) 0%, rgba(17,21,74,0.7) 100%)",
                border: `1px solid rgba(108,99,255,0.25)`,
                backdropFilter: "blur(12px)",
              }}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(108,99,255,0.2)", color: C.purple }}>
                <CalendarDays size={22} />
              </div>
              <div className="flex-1">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3" style={{ background: "rgba(108,99,255,0.15)", color: C.purple, border: `1px solid rgba(108,99,255,0.3)` }}>
                  Add-On
                </div>
                <h3 className="text-xl font-black tracking-tight mb-2" style={{ color: C.white }}>Ongoing Maintenance Retainer</h3>
                <p className="text-base mb-4 max-w-xl" style={{ color: C.dimSlate }}>
                  After your project closes, stay covered with quarterly policy reviews, up to 5 policy updates per quarter, an annual gap re-assessment, and priority access to the ManagEaze team.
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: C.slate }}>
                  {[
                    "Quarterly review cycle",
                    "Up to 5 policy updates/quarter",
                    "Annual gap re-assessment",
                    "Priority team access",
                  ].map((f) => (
                    <span key={f} className="flex items-center gap-1.5">
                      <CheckCircle2 size={11} style={{ color: C.purple }} />{f}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: C.dimSlate }}>From</div>
                <div className="text-2xl font-black" style={{ color: C.white }}>$1,000<span className="text-base font-normal" style={{ color: C.dimSlate }}>/mo</span></div>
                <div className="text-xs mt-1 mb-4" style={{ color: C.dimSlate }}>Requires active SaaS subscription</div>
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                  style={{ background: "rgba(108,99,255,0.2)", color: C.purple, border: `1px solid rgba(108,99,255,0.35)` }}
                >
                  Learn More <ArrowRight size={13} />
                </a>
              </div>
            </div>
          </InView>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="px-6 py-24" style={{ background: C.navyMid }}>
        <div className="max-w-5xl mx-auto">
          <InView>
            <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: C.purple }}>How it works</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ color: C.white }}>
              By the end of the project,<br />you're already a ManagEaze user.
            </h2>
            <p className="text-lg mb-14 max-w-xl" style={{ color: C.dimSlate }}>
              PMaaS is built around the platform. Every stage of delivery happens inside ManagEaze — so the transition from consulting client to SaaS subscriber is seamless.
            </p>
          </InView>

          <div className="relative">
            {/* Connecting line */}
            <div
              className="hidden md:block absolute top-9 left-9 right-9 h-px"
              style={{ background: `linear-gradient(90deg, transparent, ${C.border}, ${C.border}, transparent)` }}
            />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {steps.map((step, i) => (
                <InView key={step.title}>
                  <motion.div
                    variants={stagger(i * 0.08)}
                    className="flex flex-col items-center text-center md:items-start md:text-left"
                  >
                    {/* Step icon + number */}
                    <div className="relative mb-4">
                      <div
                        className="w-[72px] h-[72px] rounded-2xl flex items-center justify-center"
                        style={{
                          background: C.cardBg,
                          border: `1px solid ${C.border}`,
                          color: C.purple,
                        }}
                      >
                        {step.icon}
                      </div>
                      <div
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                        style={{ background: C.purple, color: C.white }}
                      >
                        {i + 1}
                      </div>
                    </div>
                    <div className="text-base font-bold mb-1" style={{ color: C.white }}>{step.title}</div>
                    <div className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{step.sub}</div>
                    {i < steps.length - 1 && (
                      <div className="md:hidden mt-4 flex items-center gap-1 text-xs" style={{ color: C.dimSlate }}>
                        <ChevronRight size={14} style={{ color: C.purple }} />
                      </div>
                    )}
                  </motion.div>
                </InView>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER CTA ─── */}
      <section className="px-6 py-28 text-center relative overflow-hidden" style={{ background: C.navy }}>
        {/* Orb */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div style={{ width: 700, height: 400, background: "radial-gradient(ellipse, rgba(108,99,255,0.12) 0%, transparent 70%)", filter: "blur(60px)" }} />
        </div>

        <div className="relative max-w-2xl mx-auto">
          <InView>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 text-xs font-semibold" style={{ background: "rgba(108,99,255,0.12)", border: `1px solid ${C.border}`, color: C.purple }}>
              <CalendarDays size={12} />
              Free scoping call · No commitment
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4" style={{ color: C.white }}>
              Ready to fix your<br />
              <span style={{ background: `linear-gradient(90deg, ${C.purple}, ${C.lime})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                policy situation?
              </span>
            </h2>
            <p className="text-lg mb-10" style={{ color: C.dimSlate }}>
              Book a free 30-minute scoping call. We'll listen, assess where you are, and recommend the right tier — no hard sell.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-semibold transition-all hover:opacity-90 hover:scale-[1.02]"
                style={{ background: C.purple, color: C.white, boxShadow: `0 0 50px 6px rgba(108,99,255,0.22)` }}
              >
                <CalendarDays size={17} />
                Book a Free Scoping Call
              </a>
              <NavLink
                to="/auth"
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-medium transition-all hover:opacity-80"
                style={{ color: C.slate, border: `1px solid ${C.border}` }}
              >
                Sign in to ManagEaze
                <ArrowRight size={16} />
              </NavLink>
            </div>
          </InView>
        </div>
      </section>

      {/* ─── SITE FOOTER ─── */}
      <div
        className="px-8 py-6 flex items-center justify-between text-xs"
        style={{ borderTop: "1px solid rgba(108,99,255,0.12)", color: C.dimSlate }}
      >
        <div className="flex items-center gap-2">
          <img src={logoSrc} alt="ManagEaze" style={{ height: 20, opacity: 0.6 }} />
          <span>© {new Date().getFullYear()} ManagEaze. All rights reserved.</span>
        </div>
        <NavLink to="/welcome" className="hover:opacity-80 transition-opacity">
          Back to Home
        </NavLink>
      </div>
    </div>
  );
}
